import {
  FC, useCallback, useDeferredValue, useMemo,
} from 'react';
import getClassNames from 'src/common/utils/getClassNames';
import classNames from 'classnames';
import WarningDS22 from 'src/common/components/WarningDS22';
import { useTranslation } from 'next-i18next';
import { useTripCartOptionsContext } from 'src/trip-cart/hooks/useTripCartOptionsContext';
import Button from 'src/common/components/ButtonDS22';
import { useTripCartContext } from 'src/trip-cart/hooks/useTripCartContext';
import { useTripCartBookingContext } from 'src/trip-cart/hooks/useTripCartBookingContext';
import { useForm, useFormState } from 'react-final-form';
import { TripCartFormState } from 'src/listing/types';
import { trackEvent } from 'src/common/3rdParty/tracking';
import { getListingLocationSlug } from 'src/common/helpers';
import { trackTripCartBookNowClick, trackTripCartInquiryClick } from 'src/trip-cart/utils/trackingTripCart';
import { BOOKING_MODE } from 'src/listing/constants';
import { formatDate } from 'src/common/utils/dateHelpers';
import styles from './TripCartBookingButtons.module.scss';

type TripCartBookingButtonsProps = {
  position: 'trip-cart-cost-panel' | 'trip-cart-bottom-sheet--open' | 'trip-cart-bottom-sheet--closed';
  overrideClick?: () => void;
};
const TripCartBookingButtons: FC<TripCartBookingButtonsProps> = ({ position, overrideClick }) => {
  const [t] = useTranslation('listing-page');
  const { isSubmitting } = useTripCartBookingContext();
  const { options } = useTripCartOptionsContext();
  const { listing, showAiSummary } = useTripCartContext();
  const { submit, change } = useForm<TripCartFormState>();
  const {
    values: {
      bookingMode,
      formSelectedDates,
      formDuration,
      formGuests,
      withCaptain,
      isMultiDay,
      departureTime,
    },
    errors,
  } = useFormState<TripCartFormState>();

  const instabookVisible = options.actions.instabookEnabled;
  const { inquiryEnabled } = useDeferredValue(
    options.actions,
  );

  const { isOwnBoat } = options.actions;
  const isDisabled = !inquiryEnabled || isSubmitting || isOwnBoat;

  const submitInquiry = useCallback((buttonType: 'send-inquiry-button' | 'contact-owner-button') => {
    if (!inquiryEnabled || isDisabled) {
      return;
    }

    const listingLocation = getListingLocationSlug(listing.breadcrumbs);

    // Only track the event if we have a departure time
    if (departureTime && !errors?.departureTime) {
      trackEvent('inquiry_start', {
        p1: buttonType,
        p2: position,
        p3: showAiSummary ? 'ai_summary_visible' : 'ai_summary_hidden',
        ...(listingLocation ? { listing_location: listingLocation } : {}),
      });
    }

    trackTripCartInquiryClick({
      dates: formSelectedDates.map((date) => formatDate(date)),
      duration: formDuration,
      capacity: formGuests.toString(),
      captainOption: withCaptain.toString(),
      bookNowVisible: 'false',
      bookNowEnabled: 'false',
      sameOrMultiDay: isMultiDay ? 'multi-day' : 'hourly',
    });

    change(BOOKING_MODE, 'inquiry');
    submit();
  }, [
    isDisabled,
    errors,
    change,
    formDuration,
    formGuests,
    formSelectedDates,
    inquiryEnabled,
    isMultiDay,
    listing.breadcrumbs,
    position,
    submit,
    withCaptain,
    departureTime,
    showAiSummary,
  ]);

  const submitInstabook = useCallback(() => {
    if (!instabookVisible || isDisabled) {
      return;
    }
    if (departureTime && !errors?.departureTime) {
      trackTripCartBookNowClick('success');
    } else {
      trackTripCartBookNowClick('validation-error');
    }

    change(BOOKING_MODE, 'instabook');
    submit();
  }, [instabookVisible, isDisabled, departureTime, errors?.departureTime, change, submit]);

  const inquiryAction = useMemo(
    () => (instabookVisible
      ? () => submitInquiry('contact-owner-button')
      : () => submitInquiry('send-inquiry-button')),
    [instabookVisible, submitInquiry],
  );

  return (
    <div className={getClassNames(
      styles,
      'root',
      classNames({ withWarning: isOwnBoat }),
    )}
    >
      {isOwnBoat && (
        <div className={styles.warning}>
          <WarningDS22
            iconSize="m"
            showIcon
            centered
          >
            {t('pricing-structure--owner-inquiry-warning')}
          </WarningDS22>
        </div>
      )}

      <Button
        type="button"
        variant={instabookVisible ? 'tertiary-deemphasized' : 'primary-blue'}
        data-test="booking"
        disabled={isDisabled}
        loading={isSubmitting && bookingMode === 'inquiry'}
        onClick={overrideClick || inquiryAction}
        onAuxClick={overrideClick || inquiryAction}
        icon={instabookVisible ? 'chat' : undefined}
        iconPosition={instabookVisible ? 'right' : undefined}
        size={instabookVisible ? 'x-small' : undefined}
      >
        {t('price-panel--button--booking-inquiry')}
      </Button>

      {instabookVisible && (
        <div className={getClassNames(
          styles,
          'bookNowButton',
          classNames({ withWarning: isOwnBoat }),
        )}
        >
          <Button
            type="button"
            variant="instabook-green"
            data-test="instabook"
            loading={isSubmitting && bookingMode === 'instabook'}
            disabled={isDisabled}
            onClick={overrideClick || submitInstabook}
            onAuxClick={overrideClick || submitInstabook}
          >
            {t('price-panel--button--instabook')}
          </Button>
        </div>
      )}
    </div>
  );
};

export default TripCartBookingButtons;
